import React, { useState, Children } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { typography, color } from '@primer-io/goat'

import theme from 'src/theme'

const TabTitleLink = styled.button`
	position: relative;
	z-index: 1;
	padding-bottom: 8px;
	${typography('body-medium')}

	&:not(:first-child) {
		margin-left: 16px;
	}

	color: ${color('gray-600')} !important;
	border-bottom: 2px solid transparent;
	transition:
		color 0.2s ease,
		border-bottom-color 0.2s ease;

	text-decoration: unset !important;

	&[aria-current='true'],
	&:hover,
	&:focus {
		color: ${color('gray-900')} !important;
		border-bottom-color: ${color('gray-200')};
	}

	&[aria-current='true'] {
		border-bottom-color: ${color('gray-900')};
	}

	${p => p.theme.above('large')`
    padding-bottom: 13px;

    &:not(:first-child) {
      margin-left: 32px;
    }
  `}
`

const TabTitleButton = styled.button`
	-webkit-appearance: none;

	border: 1px solid ${color('gray-900')};
	color: ${color('gray-900')};
	background: ${color('white')};
	height: 40px;
	${theme.typography.scale.caption1.regular}
	line-height: 40px;
	padding: 0px 16px;
	border-radius: 100px;

	&:not(:first-child) {
		margin-left: 12px;
	}

	&:hover {
		background: var(--color-background);
	}

	&[aria-current='true'] {
		background: var(--color-black);
		color: var(--color-white);

		&:hover {
			background: var(--color-black);
		}
	}
`

const TabTitle = ({ id, active, onClick, title, variant, ...props }) => {
	if (!id || !title) return null

	const TabTitleComponent = variant === 'underline' ? TabTitleLink : TabTitleButton

	return (
		<TabTitleComponent {...props} id={id} disabled={active} aria-current={active} onClick={onClick}>
			{title}
		</TabTitleComponent>
	)
}

TabTitle.propTypes = {
	id: PropTypes.string,
	active: PropTypes.bool,
	title: PropTypes.node,
	onClick: PropTypes.func,
}

const TabContent = styled.div`
	& > *:first-child {
		padding-top: 0px;
		margin-top: 0px;
	}
`

export const Tab = ({ id, title, active, children }) => {
	if (!id || !title || !active) return null

	return <TabContent>{children}</TabContent>
}

Tab.propTypes = {
	id: PropTypes.string,
	title: PropTypes.node,
	active: PropTypes.bool,
}

const TabTitles = styled.div`
	display: flex;
	flex-wrap: wrap;
	overflow: auto;
	align-items: center;
	padding-bottom: 24px;
	${p => p.noPadding && { paddingBottom: 0 }}

	${p => p.theme.above('medium')`
    flex-wrap: nowrap;
  `}

	${p =>
		p.variant === 'underline' &&
		`
    position: relative;
    min-width: max-content;

    ::after {
      content: '';
      position: absolute;
      bottom: ${p.noPadding ? 0 : 24}px;
      left: 0;
      width: 100%;
    }
  `}
`

const HorizontalScrollWrapper = styled.div``

export const TabsWrapper = styled.div`
	padding: ${p => (p.variant === 'underline' ? '0 20px' : '20px')};
	position: relative;
	left: -${theme.breakpoints.small.margins}px;
	width: calc(100% + 2 * ${theme.breakpoints.small.margins}px);
	border-bottom: 1px solid ${color('gray-200')};
	${p => p.variant !== 'underline' && `background: var(--color-background);`}
	${p =>
		p.variant === 'underline' &&
		`
    ${HorizontalScrollWrapper} {
      overflow-x: auto;
    }
  `}

	${p => p.theme.above('medium')`
		width: 100%;
		left: 0px;
		padding: ${p => (p.variant === 'underline' ? '0' : '24px')};
	`}
`

export const TabsHeader = ({ children, variant }) => {
	return (
		<TabsWrapper variant={variant} style={{ margin: 0 }}>
			<HorizontalScrollWrapper>
				<TabTitles variant={variant} noPadding>
					{Children.map(children, ({ props }) => (
						<TabTitle key={props.id} {...props} onClick={props?.onClick} variant={variant} />
					))}
				</TabTitles>
			</HorizontalScrollWrapper>
		</TabsWrapper>
	)
}

/* eslint-disable react/prop-types */
const Tabs = ({ initial, children, variant, noPadding }) => {
	const initialTab = initial || Children.map(children, ({ props }) => props.id)?.[0]
	const [activeTab, setActiveTab] = useState(initialTab)

	const handleClick = e => setActiveTab(e.target.id)

	return (
		<TabsWrapper variant={variant}>
			<HorizontalScrollWrapper>
				<TabTitles variant={variant} noPadding>
					{Children.map(children, ({ props }) => (
						<TabTitle
							{...props}
							active={activeTab === props.id}
							onClick={e => {
								props?.onClick?.(e)
								handleClick(e)
							}}
							variant={variant}
						/>
					))}
				</TabTitles>
			</HorizontalScrollWrapper>
			{Children.map(children, ({ props }) => (
				<Tab
					{...props}
					active={activeTab === props.id}
					onClick={e => {
						props?.onClick(e)
						handleClick(e)
					}}
				/>
			))}
		</TabsWrapper>
	)
}
/* eslint-enable react/prop-types */

Tabs.propTypes = {
	initial: PropTypes.string,
}

export default Tabs
